import React from 'react';
import type { ArrowShape as ArrowShapeType } from '../../types';
import { lineHitPolygon } from '../../utils/hitArea';

const ARROWHEAD_LENGTH_PX = 18;
const ARROWHEAD_WIDTH_PX = 14;

interface ArrowShapeProps {
  shape: ArrowShapeType;
  isEditor?: boolean;
  isSelected?: boolean;
  containerWidth?: number;
  containerHeight?: number;
  scaledStrokeWidth?: number;
}

export const ArrowShape = React.memo(function ArrowShape({
  shape,
  isEditor,
  isSelected,
  containerWidth = 800,
  containerHeight = 600,
  scaledStrokeWidth,
}: ArrowShapeProps) {
  // Direction in screen space
  const dx = (shape.x2 - shape.x1) * containerWidth;
  const dy = (shape.y2 - shape.y1) * containerHeight;
  const len = Math.sqrt(dx * dx + dy * dy);

  // Too short to render anything meaningful
  if (len < 1) return <g data-shape-id={shape.id} />;

  // Unit direction in screen space
  const ux = dx / len;
  const uy = dy / len;

  // Perpendicular direction
  const px = -uy;
  const py = ux;

  // Arrowhead tip in screen space
  const tipSx = shape.x2 * containerWidth;
  const tipSy = shape.y2 * containerHeight;

  // Only draw arrowhead if arrow is long enough
  const drawHead = len > ARROWHEAD_LENGTH_PX + 2;

  // Base of arrowhead in screen space
  const baseSx = tipSx - ux * ARROWHEAD_LENGTH_PX;
  const baseSy = tipSy - uy * ARROWHEAD_LENGTH_PX;

  // Left and right corners of arrowhead base
  const halfW = ARROWHEAD_WIDTH_PX / 2;
  const leftSx = baseSx + px * halfW;
  const leftSy = baseSy + py * halfW;
  const rightSx = baseSx - px * halfW;
  const rightSy = baseSy - py * halfW;

  // Convert arrowhead points to normalized coords
  const tipN = { x: tipSx / containerWidth, y: tipSy / containerHeight };
  const leftN = { x: leftSx / containerWidth, y: leftSy / containerHeight };
  const rightN = { x: rightSx / containerWidth, y: rightSy / containerHeight };
  const baseN = { x: baseSx / containerWidth, y: baseSy / containerHeight };

  // Line ends at the base of the arrowhead (not the tip)
  const lineEndX = drawHead ? baseN.x : shape.x2;
  const lineEndY = drawHead ? baseN.y : shape.y2;

  return (
    <g data-shape-id={shape.id}>
      {isEditor && (
        <polygon
          points={lineHitPolygon(shape.x1, shape.y1, shape.x2, shape.y2, 0.015)}
          fill="transparent"
          stroke="none"
          pointerEvents="fill"
          style={{ cursor: 'inherit' }}
        />
      )}
      <line
        x1={shape.x1}
        y1={shape.y1}
        x2={lineEndX}
        y2={lineEndY}
        stroke={shape.color}
        strokeWidth={scaledStrokeWidth ?? 2}
        vectorEffect={scaledStrokeWidth ? undefined : 'non-scaling-stroke'}
        pointerEvents="none"
      />
      {drawHead && (
        <polygon
          points={`${tipN.x},${tipN.y} ${leftN.x},${leftN.y} ${rightN.x},${rightN.y}`}
          fill={shape.color}
          pointerEvents="none"
        />
      )}
    </g>
  );
});
